import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { SmsHistoryModal } from '@/components/ui/sms-history-modal'
import { apiClient, type CustomerDetail } from '@/lib/api'
import { Phone, User, Clock, MessageSquare, Calendar, Trash2, Save, X, History, Download } from 'lucide-react'

interface CustomerDetailModalProps {
  isOpen: boolean
  onClose: () => void
  tenantId: string
  customerPhone: string
  onCustomerUpdated?: () => void
}

export function CustomerDetailModal({ 
  isOpen, 
  onClose, 
  tenantId, 
  customerPhone,
  onCustomerUpdated 
}: CustomerDetailModalProps) {
  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form state
  const [name, setName] = useState('')
  const [smsEnabled, setSmsEnabled] = useState(true)
  
  // SMS History modal state
  const [smsHistoryOpen, setSmsHistoryOpen] = useState(false)
  const [selectedVisit, setSelectedVisit] = useState<any>(null)
  
  // Download state
  const [selectedVisits, setSelectedVisits] = useState<Set<string>>(new Set())

  const fetchCustomerDetail = async () => {
    if (!customerPhone || !tenantId) return
    
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getCustomerDetail(tenantId, customerPhone)
      
      // Validate response structure
      if (!response || !response.profile) {
        throw new Error('Invalid response structure from server')
      }
      
      setCustomer(response)
      setName(response.profile?.name || '')
      setSmsEnabled(response.sms_preferences?.sms_enabled ?? true)
    } catch (err: any) {
      console.error('Failed to fetch customer detail:', err)
      setError(`Failed to load customer: ${err.message || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && customerPhone) {
      fetchCustomerDetail()
    }
  }, [isOpen, customerPhone, tenantId])

  const handleSave = async () => {
    if (!customer) return
    
    try {
      setSaving(true)
      setError(null)
      
      await apiClient.updateCustomerDetail(tenantId, customerPhone, {
        name: name.trim() || undefined,
        sms_enabled: smsEnabled
      })
      
      onCustomerUpdated?.()
      onClose()
    } catch (err: any) {
      console.error('Failed to update customer:', err)
      setError(`Failed to update customer: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    const displayPhone = decodeURIComponent(customerPhone)
    if (!customer || !confirm(`Delete customer ${displayPhone}? This will remove all their data permanently.`)) {
      return
    }
    
    try {
      setDeleting(true)
      setError(null)
      
      console.log('Deleting customer:', customerPhone, 'for tenant:', tenantId)
      const result = await apiClient.deleteCustomer(tenantId, customerPhone)
      console.log('Delete result:', result)
      
      // Show success message briefly
      alert(`Customer ${displayPhone} has been deleted successfully.`)
      
      // Close modal first, then refresh the list
      onClose()
      
      // Small delay to ensure modal closes before refreshing
      setTimeout(() => {
        onCustomerUpdated?.()
      }, 100)
    } catch (err: any) {
      console.error('Failed to delete customer:', err)
      setError(`Failed to delete customer: ${err.message || 'Unknown error'}`)
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (timestamp: string | number) => {
    if (!timestamp) return 'Never'
    const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
  }

  const handleViewHistory = (visit: any) => {
    setSelectedVisit(visit)
    setSmsHistoryOpen(true)
  }

  const handleDownloadHistory = () => {
    if (!customer || selectedVisits.size === 0) return
    
    const selectedVisitsList = customer.queue_history.filter(visit => 
      selectedVisits.has(visit.entry_id)
    )
    
    let content = `Customer History Report\n`
    content += `Customer: ${customer.profile.name || 'Anonymous'}\n`
    content += `Phone: ${decodeURIComponent(customer.profile.phone)}\n`
    content += `Generated: ${new Date().toLocaleString()}\n`
    content += `\n${'='.repeat(50)}\n\n`
    
    selectedVisitsList.forEach((visit, index) => {
      content += `Visit ${index + 1}\n`
      content += `Location: ${visit.location_id}\n`
      content += `Service: ${visit.service_id}\n`
      content += `Date: ${formatDate(visit.joined_at)}\n`
      content += `Status: ${visit.status}\n`
      if (visit.wait_time) content += `Wait Time: ${visit.wait_time} minutes\n`
      content += `\n${'-'.repeat(30)}\n\n`
    })
    
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `customer-history-${decodeURIComponent(customer.profile.phone)}-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleSelectAllVisits = (checked: boolean) => {
    if (checked && customer) {
      setSelectedVisits(new Set(customer.queue_history.map(v => v.entry_id)))
    } else {
      setSelectedVisits(new Set())
    }
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', label: string }> = {
      'completed': { variant: 'default', label: 'Completed' },
      'no_show': { variant: 'destructive', label: 'No Show' },
      'cancelled': { variant: 'secondary', label: 'Cancelled' },
      'left': { variant: 'outline', label: 'Left' },
      'waiting': { variant: 'outline', label: 'Waiting' },
      'called': { variant: 'outline', label: 'Called' },
      'being_serviced': { variant: 'outline', label: 'In Service' }
    }
    
    const config = statusMap[status] || { variant: 'outline' as const, label: status }
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Customer Details
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center">Loading customer details...</div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-600 mb-4">{error}</div>
            <Button variant="outline" onClick={fetchCustomerDetail}>
              Try Again
            </Button>
          </div>
        ) : customer ? (
          <div className="space-y-6">
            {/* Customer Profile */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="font-mono">{decodeURIComponent(customer.profile.phone)}</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Customer name (optional)"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="sms-enabled"
                    checked={smsEnabled}
                    onCheckedChange={setSmsEnabled}
                  />
                  <Label htmlFor="sms-enabled" className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    SMS Notifications Enabled
                  </Label>
                </div>

                {customer.sms_preferences.opt_out_date && (
                  <div className="text-sm text-gray-600">
                    Opted out on: {formatDate(customer.sms_preferences.opt_out_date)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customer Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-600">{customer.profile.total_visits}</div>
                  <div className="text-sm text-gray-600">Total Visits</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-600">{customer.profile.completed_services}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-red-600">{customer.profile.no_shows}</div>
                  <div className="text-sm text-gray-600">No Shows</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-600">{customer.profile.avg_wait_time}m</div>
                  <div className="text-sm text-gray-600">Avg Wait</div>
                </CardContent>
              </Card>
            </div>

            {/* Visit History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Visit History ({customer.queue_history.length} visits)
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="select-all"
                        checked={selectedVisits.size === customer.queue_history.length && customer.queue_history.length > 0}
                        onCheckedChange={handleSelectAllVisits}
                      />
                      <Label htmlFor="select-all" className="text-sm">Select All</Label>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadHistory}
                      disabled={selectedVisits.size === 0}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download ({selectedVisits.size})
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customer.queue_history.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No visit history found</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {customer.queue_history.map((visit) => (
                      <div key={visit.entry_id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <Checkbox
                          checked={selectedVisits.has(visit.entry_id)}
                          onCheckedChange={(checked) => {
                            const newSelected = new Set(selectedVisits)
                            if (checked) {
                              newSelected.add(visit.entry_id)
                            } else {
                              newSelected.delete(visit.entry_id)
                            }
                            setSelectedVisits(newSelected)
                          }}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{visit.location_id} - {visit.service_id}</div>
                          <div className="text-sm text-gray-600 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(visit.joined_at)}
                            </span>
                            {visit.wait_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {visit.wait_time}m wait
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(visit.status)}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewHistory(visit)}
                            className="flex items-center gap-1"
                          >
                            <History className="h-3 w-3" />
                            History
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? 'Deleting...' : 'Delete Customer'}
              </Button>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
        
        {/* SMS History Modal */}
        {selectedVisit && (
          <SmsHistoryModal
            isOpen={smsHistoryOpen}
            onClose={() => {
              setSmsHistoryOpen(false)
              setSelectedVisit(null)
            }}
            tenantId={tenantId}
            locationId={selectedVisit.location_id}
            serviceId={selectedVisit.service_id}
            phone={customerPhone}
            entryId={selectedVisit.entry_id}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}